/**
 * Simple i18n — dict-based with React context.
 * Lang persisted to localStorage (Telegram CloudStorage not available in all versions).
 */

import { createContext, useContext, useState, useCallback, type ReactNode, createElement } from "react";

export type Lang = "uk" | "en";

const STORAGE_KEY = "prytula_lang";

const strings = {
  uk: {
    app_title: "Фонд Притула",
    motto: "Разом до перемоги!",
    donate: "Задонатити",
    news: "Новини",
    reports: "Звіти та вплив",
    exclusive: "Ексклюзив",
    library: "Бібліотека",
    events: "Події",
    community_chat: "Спільнота",
    about: "Про фонд",
    my_donations: "Мої донати",
    back: "Назад",
    open_on_website: "Відкрити на сайті",
    loading: "Завантаження…",
    empty_state: "Поки тут нічого немає",
    coming_soon: "Незабаром",
    admin_new: "Новий запис",
    admin_edit: "Редагувати",
    admin_delete: "Видалити",
    admin_save: "Зберегти",
    admin_cancel: "Скасувати",
    admin_publish: "Опублікувати",
    admin_unpublish: "Зняти з публікації",
    admin_published: "Опубліковано",
    admin_draft: "Чернетка",
    admin_confirm_delete: "Видалити цей запис?",
    disclaimer:
      "Це неофіційний демонстраційний застосунок. Офіційний сайт фонду: prytulafoundation.org",
    lang_switch: "EN",
    home: "Головна",
    levels: "Рівні",
    fundraisers: "Збори",
    profile: "Профіль",
    news_all: "Всі",
    support: "Підтримати",
    support_tab: "Підтримка",
    goal: "Ціль",
    demo_data_note: "* Прогрес — демонстраційні дані",
    my_support: "Ваша підтримка",
    active_fundraisers: "Активні збори",
    latest_news: "Останні новини",
    see_all: "Всі →",
    request_help: "Запит допомоги",
    hotline: "Гаряча лінія",
    contacts: "Контакти",
    contact_us: "Зв'язатися",
    social_networks: "Соцмережі",
    links: "Посилання",
    admin_panel: "Панель адміністратора",
    language: "Мова",
    share: "Поділитися",
    copy_link: "Копіювати",
    copied: "Скопійовано!",
    ways_to_help: "Як допомогти",
    collected: "Зібрано",
    directions: "Напрями",
    share_news: "Поділитися новиною",
    merch: "Мерч",
    points: "балів",
    subscribe_monthly: "Оформити підписку",
    cancel_anytime: "Скасувати будь-коли",
    your_rewards: "Ваші винагороди",
    levels_and_merch: "Рівні та мерч",
    more_details: "Детальніше",
  },
  en: {
    app_title: "Prytula Foundation",
    motto: "Invincible when united!",
    donate: "Donate",
    news: "News",
    reports: "Reports & Impact",
    exclusive: "Exclusive",
    library: "Library",
    events: "Events",
    community_chat: "Community",
    about: "About",
    my_donations: "My donations",
    back: "Back",
    open_on_website: "Open on website",
    loading: "Loading…",
    empty_state: "Nothing here yet",
    coming_soon: "Coming soon",
    admin_new: "New item",
    admin_edit: "Edit",
    admin_delete: "Delete",
    admin_save: "Save",
    admin_cancel: "Cancel",
    admin_publish: "Publish",
    admin_unpublish: "Unpublish",
    admin_published: "Published",
    admin_draft: "Draft",
    admin_confirm_delete: "Delete this item?",
    disclaimer:
      "This is an unofficial demo app. Official site: prytulafoundation.org",
    lang_switch: "УК",
    home: "Home",
    levels: "Levels",
    fundraisers: "Fundraisers",
    profile: "Profile",
    news_all: "All",
    support: "Support",
    support_tab: "Support",
    goal: "Goal",
    demo_data_note: "* Progress shown is demo data",
    my_support: "Your support",
    active_fundraisers: "Active fundraisers",
    latest_news: "Latest news",
    see_all: "See all →",
    request_help: "Request help",
    hotline: "Hotline",
    contacts: "Contacts",
    contact_us: "Contact us",
    social_networks: "Social networks",
    links: "Links",
    admin_panel: "Admin panel",
    language: "Language",
    share: "Share",
    copy_link: "Copy link",
    copied: "Copied!",
    ways_to_help: "Ways to help",
    collected: "Collected",
    directions: "Directions",
    share_news: "Share the News",
    merch: "Merch",
    points: "points",
    subscribe_monthly: "Subscribe monthly",
    cancel_anytime: "Cancel anytime",
    your_rewards: "Your rewards",
    levels_and_merch: "Levels & merch",
    more_details: "More details",
  },
} satisfies Record<Lang, Record<string, string>>;

export type TranslationKey = keyof (typeof strings)["uk"];

// ─── Context ──────────────────────────────────────────────────────────────────

interface I18nCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: TranslationKey) => string;
}

const I18nContext = createContext<I18nCtx | null>(null);

function resolveInitLang(): Lang {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "uk" || stored === "en") return stored;
  return navigator.language.toLowerCase().startsWith("uk") ? "uk" : "en";
}

export function I18nProvider({ children, initialLang }: { children: ReactNode; initialLang?: Lang }) {
  const [lang, setLangState] = useState<Lang>(initialLang ?? resolveInitLang());

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem(STORAGE_KEY, l);
  }, []);

  const t = useCallback(
    (key: TranslationKey): string => strings[lang][key] ?? strings.en[key] ?? key,
    [lang],
  );

  return createElement(I18nContext.Provider, { value: { lang, setLang, t } }, children);
}

export function useI18n(): I18nCtx {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

// Convenience alias
export function useT(): I18nCtx["t"] {
  return useI18n().t;
}
